import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Divider, LinearProgress, Typography } from '@mui/material';

import { LayoutBase } from '../../layouts';
import { VMenu } from '../../components/shared';
import { ToolsListing } from '../../components/custom';
import { CustomAlert } from '../../components/shared/VAlert';

import { useAuthContext } from '../../context';
import { SubNivelAcesso } from '../../services/api/users/UserServices';
import { MetaDadosProps, MinioService } from '../../services/api/minio/Minio';
import TableFiles from '../../components/shared/TableFiles';

export const Files = () => {

	const url = useParams();
	const navigate = useNavigate();
	const { userData } = useAuthContext();
	const [data, setData] = useState<MetaDadosProps[] | null>(null);
	const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
		open: false,
		message: '',
		severity: 'success'
	});

	const pagePermissionsString: string | undefined = useMemo(() => {
		let relevantPermissionKey: keyof SubNivelAcesso | undefined;

		if (url.type === 'file') {
			relevantPermissionKey = 'arquivos';
		} else if (url.type === 'foto') {
			relevantPermissionKey = 'arquivos';
		}

		return userData?.nivel_acesso?.documentacao_tecnica?.[relevantPermissionKey as keyof SubNivelAcesso];
	}, [userData, url.type]);

	async function downloadFile(id_file: string, view?: boolean) {
		try {
			const response = await MinioService.getMetadados(id_file, view);

			if (view && view == true) {
				if (response instanceof Error) {
					setAlertInfo({ open: true, message: 'URL de download não disponível', severity: 'error' });
				} else {
					window.open(response.url, '_blank');
				}
			} else {
				if (response instanceof Error) {
					setAlertInfo({ open: true, message: response.message, severity: 'error' });
				} else {
					const url = response.url;
					const name = response.nome_arquivo;

					if (url) {
						await MinioService.downloadFile(url, name);
					} else {
						setAlertInfo({ open: true, message: 'URL de download não disponível', severity: 'error' });
					}
				}
			}
		} catch (error) {
			console.error('Erro completo no downloadFile:', error);
			setAlertInfo({ open: true, message: 'Erro ao baixar arquivo', severity: 'error' });
		}
	}

	async function fetchData(uuid: string) {
		const response = await MinioService.list(uuid);
		if (response instanceof Error) return setAlertInfo({ open: true, message: response.message, severity: 'error' });
		setData(response);
	}

	useEffect(() => {
		url.id && fetchData(url.id);
	}, []);

	function navigateFile(uuid: string) {
		navigate(`./${uuid}}`);
	}


	async function deletFile(uuid: string) {
		try {
			const response = await MinioService.delet(uuid);
			if (response instanceof Error) {
				setAlertInfo({ open: true, message: 'Erro ao deletar arquivo!', severity: 'error' });
			} else {
				setAlertInfo({ open: true, message: 'Documento deletado! ', severity: 'success' });
				url.id && fetchData(url.id);
			}
		} catch (error) {
			setAlertInfo({ open: true, message: 'Erro ao deletar arquivo! ', severity: 'error' });
			console.log('Erro ao deletar arquivo', error);
		}
	}

	return (
		<LayoutBase
			title='Arquivos'
			tollBar={
				<ToolsListing
					showNewButton={pagePermissionsString?.includes('add')}
					onClickNew={() => navigate('./new')}
				/>
			}
		>
			<Box width={'100%'}>
				<VMenu id={url.id} />
				<Typography variant='h6'>Arquivos da barragem</Typography>
				<Divider />
				<Box paddingY={2}>
					{data === null ? (
						<LinearProgress />
					) : data.length === 0 ? (
						<Typography>Nenhum arquivo encontrado.</Typography>
					) : (
						<TableFiles
							delet={deletFile}
							data={data}
							downloadFile={downloadFile}
							navigate={navigateFile}
							permissions={pagePermissionsString ?? ''}
						/>
					)}
				</Box>
			</Box>
			{alertInfo.open &&
				<CustomAlert
					message={alertInfo.message}
					severity={alertInfo.severity}
					key={alertInfo.message}
				/>
			}
		</LayoutBase>
	);
};